<template>
  <BaseDialog
    data-test-id="dialogSubscribe"
    v-model="model"
    v-bind="$attrs"
    :max-width="800"
    :min-height="550"
    icon="mdi-calendar-export"
  >
    <v-container fluid>
      <v-row>
        <v-col cols="12">
          {{ t('labels.calendar.subscribe.info') }}
        </v-col>
      </v-row>
      <v-row>
        <v-col style="margin-bottom: 0">
          <PrimarySelect
            v-model="subscriptionType"
            :label="t('labels.calendar.subscribe.selectSubscriptionType')"
            :items="calendars"
          ></PrimarySelect>
          <PrimarySelect
            v-model="eventCategory"
            :label="t('labels.eventCategory')"
            :items="eventsStore.getCategoryOptions(includeAll)"
          ></PrimarySelect>
          <PrimarySwitch
            v-if="!isSelfSchedule"
            v-model="limitToMyEvents"
            :label="t('labels.calendar.subscribe.limitToMyEvents')"
          ></PrimarySwitch>
          <PrimarySwitch
            v-if="!isSelfSchedule"
            v-model="includeOpenSlots"
            :label="t('labels.calendar.subscribe.includeOpenSlots')"
          ></PrimarySwitch>
        </v-col>
      </v-row>
      <v-row v-if="subscriptionType === CalendarSubscriptionType.WebCal">
        <v-col>
          <p class="instructions">{{ t('labels.calendar.subscribe.webcal') }}</p>
          <ButtonLink
            :href="subscribeLink"
            icon="mdi-calendar-export"
            :label="t('buttons.subscribe')"
          />
        </v-col>
      </v-row>
      <v-row v-else-if="subscriptionType === CalendarSubscriptionType.Google">
        <v-col>
          <p class="instructions">{{ t('labels.calendar.subscribe.google') }}</p>
          <ButtonLink
            target="_blank"
            :href="subscribeLink"
            icon="mdi-open-in-new"
            :label="t('buttons.openCalendar')"
          />
        </v-col>
      </v-row>
      <v-row v-else-if="subscriptionType === CalendarSubscriptionType.DownloadFile">
        <v-col>
          <p class="instructions">{{ t('labels.calendar.subscribe.download') }}</p>
          <ButtonLink :href="subscribeLink" icon="mdi-download" :label="t('buttons.download')" />
        </v-col>
      </v-row>
      <v-row v-else-if="subscriptionType === CalendarSubscriptionType.Outlook365">
        <v-col>
          <p class="instructions">{{ t('labels.calendar.subscribe.outlook365') }}</p>
          <ButtonLink
            target="_blank"
            :href="subscribeLink"
            icon="mdi-open-in-new"
            :label="t('buttons.openCalendar')"
          />
        </v-col>
      </v-row>
      <v-row v-else>
        <v-col>
          <p class="instructions">
            {{ t('labels.calendar.subscribe.copy') }}
          </p>
          <ButtonIcon
            style="margin-top: 0.25rem"
            @click="copyToClipboard"
            :text="t('buttons.copyToClipboard')"
            icon="mdi-content-copy"
          />
        </v-col>
      </v-row>
    </v-container>
  </BaseDialog>
</template>

<script setup lang="ts">
import { CalendarSubscriptionType } from '@/enums/CalendarSubscriptionType'
import { EventCategory } from '@/enums/EventCategory'
import { MessageType } from '@/enums/MessageType'
import { TableStoreId } from '@/enums/TableStoreId'
import type { IEventsStore } from '@/interfaces/api/IEventsStore'
import type { ISimpleOption } from '@/interfaces/ISimpleOption'
import { useSelfSchedulerStore } from '@/stores/db/SelfSchedulerStore'
import { TableStoreFactory } from '@/stores/db/TableStoreFactory'
import { useAppStore } from '@/stores/ui/AppStore'
import { useSettingsStore } from '@/stores/ui/SettingsStore'
import { useSnackbarStore } from '@/stores/ui/SnackbarStore'
import { computed, onUpdated, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'

// STORES, IMPORTS, & COMPOSABLES
const appStore = useAppStore()
const eventsStore = TableStoreFactory.get(TableStoreId.Events) as IEventsStore
const router = useRouter()
const selfSchedulingStore = useSelfSchedulerStore()
const settingsStore = useSettingsStore()
const snackbarStore = useSnackbarStore()

const { t } = useI18n({ useScope: 'global' })

// PROPS & EMITS
const model = defineModel<boolean>({ default: false })

// REACTIVE VARIABLES
const calendars = ref<Array<ISimpleOption>>([
  {
    title: t('labels.calendar.subscribe.options.webcal'),
    value: CalendarSubscriptionType.WebCal
  },
  { title: t('labels.calendar.subscribe.options.google'), value: CalendarSubscriptionType.Google },
  { title: t('labels.calendar.subscribe.options.copy'), value: CalendarSubscriptionType.CopyLink },
  {
    title: t('labels.calendar.subscribe.options.download'),
    value: CalendarSubscriptionType.DownloadFile
  },
  {
    title: t('labels.calendar.subscribe.options.outlook'),
    value: CalendarSubscriptionType.Outlook365
  }
])

const limitToMyEvents = ref<boolean>(false)
const includeOpenSlots = ref<boolean>(true)
const subscriptionType = ref<CalendarSubscriptionType>(CalendarSubscriptionType.WebCal)
const eventCategory = ref<EventCategory>(EventCategory.All)

// COMPUTED PROPERTIES
const includeAll = computed(() => {
  return settingsStore.globals.clearance.enabled || settingsStore.globals.training.enabled
})

const isSelfSchedule = computed(() => {
  return router.currentRoute.value.name === 'selfSchedule'
})

const subscribeLink = computed(() => {
  const host = APP_ENV.VITE_APP_FITPRO_HOST
  let link = ''
  if (isSelfSchedule.value) {
    link = `${host}/appointments.ics?key=${selfSchedulingStore.key}`
  } else {
    link = `${host}/calendar.ics?auth=${encodeURIComponent(settingsStore.sapAccountId)}&datasetid=${settingsStore.activeDatasetId}`
    if (limitToMyEvents.value && appStore.currentAdmin) {
      link += `&organizerId=${appStore.currentAdmin.id}`
    }
    if (includeOpenSlots.value) {
      link += `&open=true`
    }
  }

  link += `&locale=${settingsStore.localeCode}`

  if (eventCategory.value !== EventCategory.All) {
    link += `&category=${eventCategory.value}`
  }

  const webcalLink = link.replace(location.protocol, 'webcal:')

  switch (subscriptionType.value) {
    case CalendarSubscriptionType.DownloadFile: {
      return link
    }
    case CalendarSubscriptionType.WebCal: {
      return webcalLink
    }
    case CalendarSubscriptionType.Google: {
      return `https://calendar.google.com/calendar/u/0/r?cid=${encodeURIComponent(webcalLink)}`
    }
    case CalendarSubscriptionType.Outlook365: {
      return `https://outlook.office.com/calendar/0/addfromweb?url=${encodeURIComponent(webcalLink)}`
    }
  }
  return link
})

// LIFECYCLE HOOKS
onUpdated(() => {
  if (includeAll.value) {
    eventCategory.value = EventCategory.All
  } else {
    eventCategory.value = EventCategory.FitTest
  }
})

// FUNCTIONS
const copyToClipboard = () => {
  navigator.clipboard.writeText(subscribeLink.value)
  snackbarStore.addMessage(t('text.copiedToClipboard'), MessageType.Info)
}
</script>

<style lang="scss" scoped>
.instructions {
  margin-top: 0;
}
</style>
